import React from 'react';
import { motion } from 'framer-motion';
import { Droplets, Sparkles, Wind, Shield, Wrench, Zap } from 'lucide-react';
import GlassCard from './GlassCard';
import { services } from '../data/services';

const Services = ({ lang = 'en' }) => {
  const isAr = lang === 'ar';

  const content = {
    title: isAr ? 'خدماتنا المتميزة' : 'Our Premium Services',
    subtitle: isAr
      ? 'خدمات احترافية للعناية بالسيارات باستخدام أفضل المنتجات والتقنيات مع سوبر كلاسيكي'
      : 'Professional car care services using premium products and techniques with Super Classy',
    startingFrom: isAr ? 'يبدأ من' : 'Starting from',
    currency: isAr ? 'د.ك' : 'KD',
    bookBtn: isAr ? 'احجز الآن' : 'Book Now',
  };

  const serviceIcons = {
    'Foam Wash': <Droplets className='h-6 w-6 text-white' />,
    'غسيل فوم': <Droplets className='h-6 w-6 text-white' />,
    Waxing: <Sparkles className='h-6 w-6 text-white' />,
    'تلميع واكس': <Sparkles className='h-6 w-6 text-white' />,
    'Interior Cleaning': <Wind className='h-6 w-6 text-white' />,
    'تنظيف داخلي': <Wind className='h-6 w-6 text-white' />,
    'Engine Cleaning': <Wrench className='h-6 w-6 text-white' />,
    'تنظيف المحرك': <Wrench className='h-6 w-6 text-white' />,
    'Ceramic Coating': <Shield className='h-6 w-6 text-white' />,
    'نانو سيراميك': <Shield className='h-6 w-6 text-white' />,
    'Quick Wash': <Zap className='h-6 w-6 text-white' />,
    'غسيل سريع': <Zap className='h-6 w-6 text-white' />,
    Polishing: <Zap className='h-6 w-6 text-white' />,
    'بوليش (تلميع خارجي)': <Zap className='h-6 w-6 text-white' />,
    'Leather Seat Treatment': <Shield className='h-6 w-6 text-white' />,
    'عناية الكراسي الجلد': <Shield className='h-6 w-6 text-white' />,
    'Window Polishing': <Droplets className='h-6 w-6 text-white' />,
    'بوليش زجاج': <Droplets className='h-6 w-6 text-white' />,
    'Stains Removal': <Wind className='h-6 w-6 text-white' />,
    'إزالة البقع المستعصية': <Wind className='h-6 w-6 text-white' />,
    'AC Sanitizing': <Droplets className='h-6 w-6 text-white' />,
    'تعقيم التكييف': <Droplets className='h-6 w-6 text-white' />,
  };

  const openWhatsApp = (serviceName) => {
    const message = encodeURIComponent(
      isAr
        ? `مرحباً، أود حجز خدمة ${serviceName}.`
        : `Hi, I want to book ${serviceName} service.`,
    );
    window.open(`https://wa.me/96550040026?text=${message}`, '_blank');
  };

  return (
    <section
      id='services'
      className='section-padding py-20'
      dir={isAr ? 'rtl' : 'ltr'}
    >
      <div className='max-w-7xl mx-auto'>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className='text-center mb-12'
        >
          <h2 className='text-4xl md:text-5xl font-black mb-4 text-gradient'>
            {content.title}
          </h2>
          <p className='text-xl text-gray-400 max-w-2xl mx-auto'>
            {content.subtitle}
          </p>
        </motion.div>

        {/* The Grid Container */}
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
          {services.map((service, index) => {
            const sName = isAr ? service.nameAr || service.name : service.name;
            const sDesc = isAr
              ? service.descriptionAr || service.description
              : service.description;

            return (
              <GlassCard
                key={service.id}
                delay={index * 0.1}
                lang={lang}
                className='p-6 flex flex-col h-full group border border-white/5 bg-white/5 hover:bg-white/10 transition-colors'
              >
                {/* Icon Wrapper with the horizontal gradient bar effect from your screenshot */}
                <div className='relative mb-6'>
                  <div
                    className={`inline-flex p-3 premium-gradient rounded-xl shadow-lg relative z-10 group-hover:scale-110 transition-transform duration-300 ${isAr ? 'ml-auto' : 'mr-auto'}`}
                  >
                    {serviceIcons[sName] || (
                      <Droplets className='h-6 w-6 text-white' />
                    )}
                  </div>
                  {/* Subtle glowing bar behind the icon as seen in the JPG */}
                  <div className='absolute top-1/2 left-0 w-full h-8 premium-gradient blur-3xl opacity-20 -translate-y-1/2 -z-0' />
                </div>

                <h3 className='text-xl font-black mb-2 text-white'>{sName}</h3>
                <p className='text-gray-400 text-sm mb-8 leading-relaxed font-medium line-clamp-3'>
                  {sDesc}
                </p>

                {/* Footer: Price and Button aligned horizontally */}
                <div
                  className={`mt-auto pt-4 border-t border-white/10 flex items-center justify-between ${isAr ? 'flex-row-reverse' : ''}`}
                >
                  <div
                    className={`flex flex-col ${isAr ? 'text-right' : 'text-left'}`}
                  >
                    <span className='text-[10px] font-bold text-gray-500 uppercase tracking-tighter'>
                      {content.startingFrom}
                    </span>
                    <span className='text-lg font-black text-royal-purple-400'>
                      {isAr
                        ? `${service.price} ${content.currency}`
                        : `${content.currency} ${service.price}`}
                    </span>
                  </div>

                  <button
                    onClick={() => openWhatsApp(sName)}
                    className='px-6 py-2 premium-gradient text-white rounded-lg text-xs font-black shadow-lg hover:shadow-royal-purple-500/30 transition-all active:scale-95'
                  >
                    {content.bookBtn}
                  </button>
                </div>
              </GlassCard>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
